/**
 * MobileCollabsList
 *
 * Mobile card-based replacement for CollabsDataTable.
 * Shows each collaborator as a touch-friendly card with key info.
 * Supports search, "show new only" toggle, pagination, and action buttons.
 */
import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Divider,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MergeOutlinedIcon from "@mui/icons-material/MergeOutlined";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Langs, { Localize } from "../../../app/lang/langs";
import { checkPermission, getTagClass } from "../../../app/utils/helpers";
import { getCollabStatusMuiColor, isCollabPlannable } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

// Map PrimeReact Tag severity names to MUI Chip colors
const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

const PAGE_SIZE = 20;

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref (kept for interface compat)
 * @param {Array}  props.data - collaborators list
 * @param {Array}  props.allCollabsList - full unfiltered list
 * @param {string} props.scrollHeight - CSS height (kept for interface compat)
 * @param {Function} props.onRowDoubleClick - view collab details
 * @param {Function} props.onFusion - merge/fusion action
 * @param {Function} props.onViewPlanning - view planning action
 * @param {Function} props.onFilteredDataChange - called with filtered data
 */
export default function MobileCollabsList({
  apiRef,
  data,
  allCollabsList,
  scrollHeight,
  onRowDoubleClick,
  onFusion,
  onViewPlanning,
  onFilteredDataChange,
}) {
  const lang = useSelector((state) => state.settings.lang);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const navigate = useNavigate();
  const canAccessClientsTeams = checkPermission(config.scopes.clientsTeams);
  const [searchText, setSearchText] = useState("");
  const [showNewOnly, setShowNewOnly] = useState(false);
  const [page, setPage] = useState(1);

  const newCollabsList = useMemo(
    () => (allCollabsList || []).filter((c) => c.is_new),
    [allCollabsList]
  );

  const baseData = showNewOnly ? newCollabsList : data || [];

  // Filter data by search text across key fields
  const filteredData = useMemo(() => {
    if (!searchText.trim()) {
      return baseData;
    }
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return baseData.filter((collab) => {
      const searchStr = [
        collab.nom,
        collab.prenom,
        collab.matricule,
        collab.type_collab,
        collab.statut,
        collab.current_team_name,
        collab.type_contrat,
        collab.email,
        collab.company,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((term) => searchStr.includes(term));
    });
  }, [baseData, searchText]);

  // Notify parent of filtered data changes
  const notifyFilteredData = useCallback(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredData);
    }
  }, [filteredData, onFilteredDataChange]);

  React.useEffect(() => {
    notifyFilteredData();
  }, [notifyFilteredData]);

  // Reset page when filter changes
  React.useEffect(() => {
    setPage(1);
  }, [searchText, showNewOnly]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getStatusColor = (statut) => getCollabStatusMuiColor(statut);

  const getTypeChipColor = (type) => {
    const severity = getTagClass(type);
    return severityToColor[severity] || "default";
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        // Clears the home indicator so the pagination row stays tappable.
        pb: "calc(20px + env(safe-area-inset-bottom))",
        mt: "130px",
        // 100vh overshoots the visual viewport on iOS Safari (the browser toolbar is
        // not subtracted), which pushed the pagination row permanently off-screen in
        // this fixed-height, non-page-scrolling layout. dvh tracks the visual
        // viewport; the vh declaration stays as the fallback.
        height: "calc(100vh - 130px)",
        "@supports (height: 100dvh)": { height: "calc(100dvh - 130px)" },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {/* Toggle for new collabs */}
      {newCollabsList.length > 0 && (
        <FormControlLabel
          control={
            <Switch
              checked={showNewOnly}
              onChange={(e) => setShowNewOnly(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              <Langs str="Show new collabs only" /> ({newCollabsList.length})
            </Typography>
          }
          sx={{ mb: 0.5, ml: 0 }}
        />
      )}

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, px: 0.5, fontWeight: 500, letterSpacing: "0.02em", opacity: 0.7 }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((collab, index) => (
          <Fade in key={collab.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onRowDoubleClick(collab)}
                sx={{ p: 0 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Top row: name + status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      {/* Avatar circle */}
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          bgcolor: collab.type_collab === "externe" ? "error.light" : "warning.light",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          flexShrink: 0,
                        }}
                      >
                        {(collab.prenom?.[0] || "").toUpperCase()}
                        {(collab.nom?.[0] || "").toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.01em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {collab.prenom} {collab.nom}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem", opacity: 0.75 }}
                        >
                          {collab.matricule}
                          {collab.company ? ` - ${collab.company}` : ""}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status chip */}
                    {collab.is_new ? (
                      <Chip
                        label={<Langs str="New" />}
                        size="small"
                        color="info"
                        sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24, borderRadius: "8px" }}
                      />
                    ) : collab.statut ? (
                      <Chip
                        label={collab.statut}
                        size="small"
                        color={getStatusColor(collab.statut)}
                        variant={isCollabPlannable(collab.statut) ? "filled" : "outlined"}
                        sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24, borderRadius: "8px" }}
                      />
                    ) : null}
                  </Box>

                  {/* Tags row */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {collab.type_collab && (
                      <Chip
                        label={Localize(collab.type_collab)}
                        size="small"
                        color={getTypeChipColor(collab.type_collab)}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 24, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                      />
                    )}
                    {collab.current_team_name && (
                      <Chip
                        label={collab.current_team_name}
                        size="small"
                        variant="outlined"
                        clickable={canAccessClientsTeams}
                        onClick={canAccessClientsTeams ? (e) => {
                          e.stopPropagation();
                          const encodedLabel = encodeURIComponent(collab.current_team_name);
                          navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
                        } : undefined}
                        sx={{ fontSize: "0.7rem", height: 24, maxWidth: 160, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                      />
                    )}
                    {collab.type_contrat && (
                      <Chip
                        label={collab.type_contrat}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 24, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                      />
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>

              {/* Action buttons row */}
              <Divider sx={{ opacity: 0.4, borderColor: "rgba(0,0,0,0.06)" }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                {collab.is_new ? (
                  <Tooltip title={<Langs str="Merge" />}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFusion(collab);
                      }}
                      aria-label={Localize("Merge")}
                      sx={{
                        minWidth: 44,
                        minHeight: 44,
                        borderRadius: "12px",
                        transition: TRANSITION_SMOOTH,
                        "&:hover": {
                          backgroundColor: "rgba(255,144,21,0.08)",
                          transform: "scale(1.08)",
                        },
                        "&:active": { transform: "scale(0.95)" },
                      }}
                    >
                      <MergeOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  collab.current_id_team && (
                    <Tooltip title={<Langs str="VIEW_PLANNING" />}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewPlanning(collab);
                        }}
                        aria-label={Localize("VIEW_PLANNING")}
                        sx={{
                          minWidth: 44,
                          minHeight: 44,
                          borderRadius: "12px",
                          transition: TRANSITION_SMOOTH,
                          "&:hover": {
                            backgroundColor: "rgba(255,144,21,0.08)",
                            transform: "scale(1.08)",
                          },
                          "&:active": { transform: "scale(0.95)" },
                        }}
                      >
                        <CalendarMonthOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                )}
              </Box>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                minWidth: 40,
                height: 40,
                margin: "0 3px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
